/**
 * ConfigRegistry — mapeia nodeType → componente React de configuração.
 *
 * Lazy-loaded via React.lazy para code-splitting automático.
 * O NodeConfigPanel usa este registry para renderizar o formulário
 * correto quando o usuário clica em um node no canvas.
 *
 * Para adicionar um novo node type:
 * 1. Crie o componente em config/{categoria}/{NodeType}Config.tsx
 * 2. Adicione o lazy import aqui
 * 3. Registre no MAP abaixo
 */

import { lazy, type ComponentType } from 'react';

import type { NodeConfigProps } from './types';

type LazyConfig = React.LazyExoticComponent<ComponentType<NodeConfigProps>>;

// ============================================================================
// Lazy imports — 1 por node type
// ============================================================================

// Triggers
const EventTriggerConfig = lazy(() => import('./triggers/EventTriggerConfig'));
const ScheduleTriggerConfig = lazy(() => import('./triggers/ScheduleTriggerConfig'));
const WebhookTriggerConfig = lazy(() => import('./triggers/WebhookTriggerConfig'));
const DateFieldTriggerConfig = lazy(() => import('./triggers/DateFieldTriggerConfig'));
const InactivityTriggerConfig = lazy(() => import('./triggers/InactivityTriggerConfig'));
const InstagramCommentTriggerConfig = lazy(() => import('./triggers/InstagramCommentTriggerConfig'));
const InstagramMentionTriggerConfig = lazy(() => import('./triggers/InstagramMentionTriggerConfig'));

// Actions
const SendMessageConfig = lazy(() => import('./actions/SendMessageConfig'));
const SendEmailConfig = lazy(() => import('./actions/SendEmailConfig'));
const SendTemplateConfig = lazy(() => import('./actions/SendTemplateConfig'));
const SendMediaConfig = lazy(() => import('./actions/SendMediaConfig'));
const HttpRequestConfig = lazy(() => import('./actions/HttpRequestConfig'));
const VoiceCloneConfig = lazy(() => import('./actions/VoiceCloneConfig'));
const VoiceTtsConfig = lazy(() => import('./actions/VoiceTtsConfig'));
const VoiceCloneDeleteConfig = lazy(() => import('./actions/VoiceCloneDeleteConfig'));
const MirrorMediaConfig = lazy(() => import('./actions/MirrorMediaConfig'));
const RetryScopeConfig = lazy(() => import('./control/RetryScopeConfig'));
const QueryDatabaseConfig = lazy(() => import('./actions/QueryDatabaseConfig'));
const CreateLeadConfig = lazy(() => import('./actions/CreateLeadConfig'));
const UpdateLeadConfig = lazy(() => import('./actions/UpdateLeadConfig'));
const UpdateContactConfig = lazy(() => import('./actions/UpdateContactConfig'));
const AddTagConfig = lazy(() => import('./actions/AddTagConfig'));
const RemoveTagConfig = lazy(() => import('./actions/RemoveTagConfig'));
const AssignConfig = lazy(() => import('./actions/AssignConfig'));
const CreateConversationConfig = lazy(() => import('./actions/CreateConversationConfig'));
const TransferConversationConfig = lazy(() => import('./actions/TransferConversationConfig'));
const SatisfactionSurveyConfig = lazy(() => import('./actions/SatisfactionSurveyConfig'));
const AskQuestionConfig = lazy(() => import('./actions/AskQuestionConfig'));
const AskFormConfig = lazy(() => import('./actions/AskFormConfig'));
const NextNumberConfig = lazy(() => import('./actions/NextNumberConfig'));
const SaveFormResponseConfig = lazy(() => import('./actions/SaveFormResponseConfig'));
const CreateTicketConfig = lazy(() => import('./actions/CreateTicketConfig'));
const SetVariableConfig = lazy(() => import('./actions/SetVariableConfig'));
const InternalNotificationConfig = lazy(() => import('./actions/InternalNotificationConfig'));
const FindLeadsConfig = lazy(() => import('./actions/FindLeadsConfig'));
const CreateDatabaseDocumentConfig = lazy(() => import('./actions/CreateDatabaseDocumentConfig'));
const CreateChecklistConfig = lazy(() => import('./actions/CreateChecklistConfig'));
const FindUnitConfig = lazy(() => import('./actions/FindUnitConfig'));
const FindUserConfig = lazy(() => import('./actions/FindUserConfig'));
const FindContactConfig = lazy(() => import('./actions/FindContactConfig'));
const UrlToPdfConfig = lazy(() => import('./actions/UrlToPdfConfig'));
const NfePdfConfig = lazy(() => import('./actions/NfePdfConfig'));

// Control
const IfConfig = lazy(() => import('./control/IfConfig'));
const SwitchConfig = lazy(() => import('./control/SwitchConfig'));
const WaitForConfig = lazy(() => import('./control/WaitForConfig'));
const LoopConfig = lazy(() => import('./control/LoopConfig'));
const SplitConfig = lazy(() => import('./control/SplitConfig'));

// AI
const AIAgentConfig = lazy(() => import('./ai/AIAgentConfig'));
const AIAgentInlineConfig = lazy(() => import('./ai/AIAgentInlineConfig'));

// Skill I/O (#238) — nodes Entrada/Saída de skills. Os componentes vivem no
// módulo skills (config tipada por SkillInput/OutputConfigShape); cast para o
// contrato NodeConfigProps do registry (o config em runtime tem o shape certo).
const SkillInputConfig = lazy(() =>
  import('@/modules/skills/components/SkillInputNodeConfig').then((m) => ({
    default: m.default as unknown as ComponentType<NodeConfigProps>,
  })),
);
const SkillOutputConfig = lazy(() =>
  import('@/modules/skills/components/SkillOutputNodeConfig').then((m) => ({
    default: m.default as unknown as ComponentType<NodeConfigProps>,
  })),
);

// ============================================================================
// Registry Map
// ============================================================================

const CONFIG_MAP: Record<string, LazyConfig> = {
  // Triggers
  trigger_event: EventTriggerConfig,
  trigger_schedule: ScheduleTriggerConfig,
  trigger_webhook: WebhookTriggerConfig,
  trigger_date_field: DateFieldTriggerConfig,
  trigger_inactivity: InactivityTriggerConfig,
  trigger_instagram_comment: InstagramCommentTriggerConfig,
  trigger_instagram_mention: InstagramMentionTriggerConfig,

  // Actions
  action_send_message: SendMessageConfig,
  action_send_email: SendEmailConfig,
  action_send_template: SendTemplateConfig,
  action_send_media: SendMediaConfig,
  action_http_request: HttpRequestConfig,
  action_voice_clone: VoiceCloneConfig,
  action_voice_tts: VoiceTtsConfig,
  action_voice_clone_delete: VoiceCloneDeleteConfig,
  action_mirror_media: MirrorMediaConfig,
  action_query_database: QueryDatabaseConfig,
  action_create_lead: CreateLeadConfig,
  action_update_lead: UpdateLeadConfig,
  action_update_contact: UpdateContactConfig,
  action_add_tag: AddTagConfig,
  action_remove_tag: RemoveTagConfig,
  action_assign: AssignConfig,
  action_create_conversation: CreateConversationConfig,
  action_transfer_conversation: TransferConversationConfig,
  action_satisfaction_survey: SatisfactionSurveyConfig,
  action_ask_question: AskQuestionConfig,
  action_ask_form: AskFormConfig,
  action_next_number: NextNumberConfig,
  action_save_form_response: SaveFormResponseConfig,
  action_create_ticket: CreateTicketConfig,
  action_set_variable: SetVariableConfig,
  action_internal_notification: InternalNotificationConfig,
  action_find_leads: FindLeadsConfig,
  action_create_database_document: CreateDatabaseDocumentConfig,
  action_create_checklist: CreateChecklistConfig,
  action_find_unit: FindUnitConfig,
  action_find_user: FindUserConfig,
  action_find_contact: FindContactConfig,
  action_url_to_pdf: UrlToPdfConfig,
  action_nfe_pdf: NfePdfConfig,

  // Control
  control_if: IfConfig,
  control_switch: SwitchConfig,
  control_wait_for: WaitForConfig,
  control_loop: LoopConfig,
  control_retry_scope: RetryScopeConfig,
  control_split: SplitConfig,

  // AI
  ai_agent: AIAgentConfig,
  ai_agent_inline: AIAgentInlineConfig,

  // Skill I/O (#238)
  skill_input: SkillInputConfig,
  skill_output: SkillOutputConfig,
};

/**
 * Retorna o componente de configuração para um nodeType.
 * Retorna undefined se o tipo não está registrado.
 */
export function getConfigComponent(nodeType: string): LazyConfig | undefined {
  return CONFIG_MAP[nodeType];
}

/**
 * Lista todos os node types registrados.
 */
export function getRegisteredNodeTypes(): string[] {
  return Object.keys(CONFIG_MAP);
}
